import {
  API_DATETIME_EMPTY,
  MOMENT_FMT_MMM_DD_YYYY,
  MOMENT_FMT_MMM_DD_YYYY_H_MM_A,
  ddMmYyyyToYmd,
  formatLocalDateTime,
} from "utils/dateTime";
import { getFileExtension, getFileExtensionLower } from "utils/files";
import { getFileTypeIcon } from "utils/fileTypeIcons";

export { getFileExtension, getFileTypeIcon };

const IMAGE_EXTENSIONS = new Set(
  ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].map((e) => e.toLowerCase())
);

const TEXT_EXTENSIONS = new Set(
  ["txt", "csv", "json", "xml", "md", "log", "rtf", "yaml", "yml"].map((e) => e.toLowerCase())
);

const NO_PREVIEW_EXTENSIONS = new Set(["fvu", "fuv", "csi"].map((e) => e.toLowerCase()));

const SPREADSHEET_EXTENSIONS = new Set(["xls", "xlsx"].map((e) => e.toLowerCase()));

export const TEXT_PREVIEW_MAX_CHARS = 500_000;

export function isImageFile(fileName: string): boolean {
  return IMAGE_EXTENSIONS.has(getFileExtensionLower(fileName));
}

export function isPdfFile(fileName: string): boolean {
  return getFileExtensionLower(fileName) === "pdf";
}

export function isDocxFile(fileName: string): boolean {
  return getFileExtensionLower(fileName) === "docx";
}

export function isTextFile(fileName: string): boolean {
  return TEXT_EXTENSIONS.has(getFileExtensionLower(fileName));
}

export function isPreviewUnavailableFile(fileName: string): boolean {
  return NO_PREVIEW_EXTENSIONS.has(getFileExtensionLower(fileName));
}

export function isSpreadsheetFile(fileName: string): boolean {
  return SPREADSHEET_EXTENSIONS.has(getFileExtensionLower(fileName));
}

export function resolvePreviewFileName(fileName: string, docPath?: string): string {
  const name = fileName.trim();
  if (/\.[a-zA-Z0-9]+$/.test(name)) return name;
  const fromPath = docPath?.trim() ? (docPath.split("/").pop()?.trim() ?? "") : "";
  if (/\.[a-zA-Z0-9]+$/.test(fromPath)) return fromPath;
  return name || fromPath || "document";
}

const CASE_DOCUMENT_PATH_DATE = /\/(\d{2})-(\d{2})-(\d{4})\//;

export function parseDateFromCaseDocumentPath(documentPath?: string): string | undefined {
  const p = documentPath?.trim();
  if (!p) return undefined;
  const match = p.match(CASE_DOCUMENT_PATH_DATE);
  if (!match) return undefined;
  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900) return undefined;
  return ddMmYyyyToYmd(`${dd}-${mm}-${yyyy}`) ?? `${yyyy}-${mm}-${dd}`;
}

export type FormatDocumentUploadDateOptions = {
  dateOnly?: boolean;
};

export function formatDocumentUploadDate(
  dateStr?: string,
  documentPathFallback?: string,
  options?: FormatDocumentUploadDateOptions
): string {
  const dateFmt = options?.dateOnly ? MOMENT_FMT_MMM_DD_YYYY : MOMENT_FMT_MMM_DD_YYYY_H_MM_A;
  const trimmed = dateStr?.trim();
  if (trimmed) {
    const formatted = formatLocalDateTime(trimmed, { format: dateFmt, invalidValue: "" });
    if (formatted) return formatted;
  }
  const fromPath = parseDateFromCaseDocumentPath(documentPathFallback);
  if (fromPath) {
    return formatLocalDateTime(fromPath, { format: MOMENT_FMT_MMM_DD_YYYY });
  }
  return API_DATETIME_EMPTY;
}
